import { useCallback, useEffect, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Shell, Btn, Field, dateRange, fmtDate } from "@/components/album/ui";
import { adminActivate, adminDelete, adminList } from "@/lib/api";
import { buildCard, saveBlob, shareCard } from "@/lib/albumCard";
import { typeOf, filterOf } from "@/config/album";

const Card = ({ r, pw, onChange }) => {
  const canvasWrap = useRef(null);
  const [busy, setBusy] = useState(false);
  const type = typeOf(r.eventType);
  const owner = type.owner; // "pengantin" hanya untuk pernikahan
  const origin = window.location.origin;
  const guestLink = `${origin}/a/${r.id}`;
  const coupleLink = `${origin}/kelola/${r.id}`;
  const daysLeft = r.expiresAt ? Math.ceil((r.expiresAt - Date.now()) / 86400000) : 0;
  const slug = (r.couple || r.eventName).replace(/\W+/g, "-");

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await onChange();
    } catch (e) {
      toast.error(e.message);
    }
    setBusy(false);
  };
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Disalin"));
  const downloadQR = () => {
    const c = canvasWrap.current?.querySelector("canvas");
    if (!c) return;
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `qr-${slug}.png`;
    a.click();
  };

  const waText = `Halo, albummu di DIRGA Ruang Kenang sudah aktif.

Link album tamu:
${guestLink}

Link kelola album (khusus ${owner}):
${coupleLink}

PIN: ${r.pin}

Aktif sampai ${fmtDate(r.expiresAt)}. Kartu QR terlampir, silakan dicetak dan taruh di meja tamu.`;

  // Kartu QR siap cetak (PNG 10 x 15 cm)
  const makeCard = () =>
    buildCard({
      title: r.couple || r.eventName,
      subtitle: r.couple ? r.eventName : "",
      dateText: dateRange(r),
      qrCanvas: canvasWrap.current?.querySelector("canvas"),
    });

  const downloadCard = async () => {
    setBusy(true);
    try {
      saveBlob(await makeCard(), `kartu-${slug}.png`);
      toast.success("Kartu diunduh");
    } catch {
      toast.error("Kartu gagal dibuat. Coba lagi.");
    }
    setBusy(false);
  };

  // Kirim kartu (gambar) + pesan (teks) sekaligus lewat menu bagikan
  const sendWA = async () => {
    navigator.clipboard?.writeText(waText).catch(() => {}); // cadangan: pesan tersalin
    setBusy(true);
    try {
      const mode = await shareCard({ blob: await makeCard(), filename: `kartu-${slug}.png`, text: waText });
      if (mode === "saved") {
        window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, "_blank", "noopener");
        toast.message("Kartu terunduh. Lampirkan gambarnya di WhatsApp (pesan juga sudah tersalin).");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Gagal menyiapkan kartu. Coba lagi.");
    }
    setBusy(false);
  };

  return (
    <div className="border border-[#E8E3D9] bg-white p-6">
      <div className="flex gap-5">
        <img src={r.cover} alt="" className="h-24 w-24 object-cover" />
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-2xl">{r.couple || r.eventName}</h3>
          <p className="font-sans text-sm text-[#5C5C5C]">{r.couple ? r.eventName : type.label}</p>
          <p className="mt-1 font-sans text-xs text-[#8A8A8A]">
            {type.label} · {dateRange(r)} · Foto tamu lain: {r.allowGuestView ? "boleh dilihat" : "tidak boleh"} · Nuansa {filterOf(r.filter).label} · Kode {r.rid}
          </p>
        </div>
      </div>

      {r.status === "pending" ? (
        <div className="mt-5 flex gap-3">
          <Btn className="py-3" disabled={busy} onClick={() => run(() => adminActivate(pw, r.rid), "Album diaktifkan")}>Aktifkan</Btn>
          <Btn variant="outline" className="py-3" disabled={busy} onClick={() => window.confirm("Hapus permintaan ini?") && run(() => adminDelete(pw, r.rid))}>Hapus</Btn>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 border-t border-[#E8E3D9] pt-6 sm:grid-cols-[160px_1fr]">
          <div>
            <div ref={canvasWrap}>
              <QRCodeCanvas value={guestLink} size={1024} marginSize={2} style={{ width: 160, height: 160 }} />
            </div>
            <button type="button" onClick={downloadQR} className="mt-2 font-sans text-xs uppercase tracking-widest underline">Unduh QR</button>
          </div>
          <div className="space-y-3 font-sans text-sm">
            <p><span className="text-[#8A8A8A]">PIN {owner}:</span> <b className="text-lg tracking-[0.3em]">{r.pin}</b></p>
            <p className="break-all"><span className="text-[#8A8A8A]">Link tamu:</span> {guestLink}</p>
            <p className="break-all"><span className="text-[#8A8A8A]">Link {owner}:</span> {coupleLink}</p>
            <p className="text-[#8A8A8A]">{r.photoCount} foto · {daysLeft > 0 ? `Sisa ${daysLeft} hari` : "Sudah berakhir"}</p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Btn className="px-5 py-3" disabled={busy} onClick={sendWA}>Kirim lewat WA</Btn>
              <Btn variant="outline" className="px-5 py-3" disabled={busy} onClick={downloadCard}>Unduh kartu</Btn>
              <Btn variant="outline" className="px-5 py-3" onClick={() => copy(waText)}>Salin pesan</Btn>
              <Btn variant="outline" className="px-5 py-3" disabled={busy} onClick={() => window.confirm("Hapus album dan semua fotonya?") && run(() => adminDelete(pw, r.rid))}>Hapus</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [pw, setPw] = useState(sessionStorage.getItem("dirga:adminpw") || "");
  const [input, setInput] = useState("");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (password) => {
    const data = await adminList(password);
    setItems(data);
  }, []);

  useEffect(() => {
    if (!pw) return;
    load(pw).catch((e) => {
      if (e.status === 401) {
        sessionStorage.removeItem("dirga:adminpw");
        setPw("");
      } else toast.error(e.message);
    });
  }, [pw, load]);

  const login = async () => {
    setLoading(true);
    try {
      await load(input);
      sessionStorage.setItem("dirga:adminpw", input);
      setPw(input);
    } catch (e) {
      toast.error(e.status === 401 ? "Password salah" : e.message);
    }
    setLoading(false);
  };

  if (!pw)
    return (
      <Shell className="max-w-sm pt-16">
        <Toaster position="top-center" />
        <h1 className="font-serif text-4xl font-light">Admin</h1>
        <div className="mt-8 space-y-6">
          <Field type="password" label="Password" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} />
          <Btn onClick={login} disabled={loading || !input}>{loading ? "Memeriksa…" : "Masuk"}</Btn>
        </div>
      </Shell>
    );

  const pending = (items || []).filter((i) => i.status === "pending");
  const active = (items || []).filter((i) => i.status === "active");
  const refresh = () => load(pw);
  return (
    <Shell className="max-w-3xl pt-8">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between">
        <h1 className="font-serif text-4xl font-light">Permintaan Album</h1>
        <button type="button" onClick={() => { sessionStorage.removeItem("dirga:adminpw"); setPw(""); setItems(null); }} className="font-sans text-xs uppercase tracking-widest underline">Keluar</button>
      </div>
      {items === null ? (
        <p className="mt-10 font-sans text-sm text-[#8A8A8A]">Memuat…</p>
      ) : (
        <>
          <h2 className="mt-10 font-sans text-xs uppercase tracking-[0.3em]">Menunggu ({pending.length})</h2>
          <div className="mt-4 space-y-4">{pending.map((r) => <Card key={r.rid} r={r} pw={pw} onChange={refresh} />)}</div>
          <h2 className="mt-12 font-sans text-xs uppercase tracking-[0.3em]">Aktif ({active.length})</h2>
          <div className="mt-4 space-y-4">{active.map((r) => <Card key={r.rid} r={r} pw={pw} onChange={refresh} />)}</div>
        </>
      )}
    </Shell>
  );
}
